import React, { useEffect } from 'react';
import type { JsonLdRecord } from './types';

type Props = {
  data: JsonLdRecord | JsonLdRecord[];
};

/** Inject JSON-LD into document head; cleans up on unmount. */
export function JsonLd({ data }: Props) {
  useEffect(() => {
    const payloads = Array.isArray(data) ? data : [data];
    const nodes: HTMLScriptElement[] = [];
    for (const payload of payloads) {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-tw-jsonld', '1');
      el.text = JSON.stringify(payload);
      document.head.appendChild(el);
      nodes.push(el);
    }
    return () => {
      for (const el of nodes) {
        el.parentNode?.removeChild(el);
      }
    };
  }, [data]);

  return null;
}

export function breadcrumbJsonLd(items: { name: string; url: string }[]): JsonLdRecord {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function organizationJsonLd(): JsonLdRecord {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'TinyWins.ai',
    url: 'https://www.tinywins.ai',
    logo: 'https://www.tinywins.ai/og/tinywins-og.jpg',
    email: 'info@tinywins.ai',
    description:
      'TinyWins.ai helps parents and therapists support neurodivergent children with personalized milestones, sensory-aware strategies, and progress tracking.',
  };
}

export function websiteJsonLd(): JsonLdRecord {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'TinyWins.ai',
    url: 'https://www.tinywins.ai',
    description:
      'Personalized developmental support for neurodivergent children — goal tracking, educational resources, and collaboration with care teams.',
  };
}

export function softwareApplicationJsonLd(): JsonLdRecord {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'TinyWins',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'Web',
    url: 'https://www.tinywins.ai',
    description:
      'Educational developmental support app for families of neurodivergent children: milestone and goal tracking, sensory-aware strategies, and parent–therapist collaboration.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      description: 'Free parent account available',
    },
  };
}
